import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { format, addDays, subDays, parseISO, isWeekend } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useUnits } from '@/hooks/api/useProperties';
import { useRatePlans } from '@/hooks/api/useRevenue';
import { useOverridePrice } from '@/hooks/api/usePricing';
import { useQueryClient } from '@tanstack/react-query';
import { useSyncSnapshot } from '@/hooks/api/useChannelSync';
import { resolvePrimaryOta, type SyncCell } from '@/services/api/channelSync.api';
import { ParityBadge, isBadgeableState, parityStateLabel } from '@/components/channels/parityState';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Unit {
  id: string;
  name: string;
  type: string;
  basePrice: number;
  weekendPrice: number | null;
  capacity: number;
}

interface EditingCell {
  unitId: string;
  date: string;
  currentPrice: number;
}

interface BulkEditState {
  isOpen: boolean;
  fromDate: string;
  toDate: string;
  selectedUnitIds: string[];
  newPrice: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateDateRange(start: Date, days: number): Date[] {
  return Array.from({ length: days }, (_, i) => addDays(start, i));
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RateCalendarPage() {
  const propertyId = useSelectedPropertyId();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Deep-link from the Sync panel: /rate-calendar?unit=…&date=YYYY-MM-DD lands
  // the window on that date.
  const deepLinkDate = (() => {
    const raw = searchParams.get('date');
    if (!raw) return null;
    const d = parseISO(raw);
    return isNaN(d.getTime()) ? null : d;
  })();

  // Date navigation state
  const [viewDays, setViewDays] = useState<7 | 14>(14);
  const [startDate, setStartDate] = useState<Date>(() => deepLinkDate ?? new Date());
  const [datePickerValue, setDatePickerValue] = useState(() =>
    format(deepLinkDate ?? new Date(), 'yyyy-MM-dd'),
  );

  // Rate plan filter
  const [selectedRatePlanId, setSelectedRatePlanId] = useState<string>('');

  // Editing state
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [overrides, setOverrides] = useState<Map<string, number>>(new Map());

  // Bulk edit state
  const [bulkEdit, setBulkEdit] = useState<BulkEditState>({
    isOpen: false,
    fromDate: format(new Date(), 'yyyy-MM-dd'),
    toDate: format(addDays(new Date(), 6), 'yyyy-MM-dd'),
    selectedUnitIds: [],
    newPrice: '',
  });

  // Data fetching
  const { data: unitsData, isLoading: unitsLoading } = useUnits(propertyId || undefined);
  const { data: ratePlansData } = useRatePlans({
    propertyId,
    limit: 100,
  });

  const overrideMutation = useOverridePrice();

  // Parse units from response
  const units: Unit[] = useMemo(() => {
    if (!unitsData) return [];
    const raw = unitsData as { data?: any[] } | any[];
    const list = Array.isArray(raw) ? raw : ('data' in raw && Array.isArray(raw.data) ? raw.data : []);
    return list.map((u: any) => ({
      id: u.id,
      name: u.name,
      type: u.type,
      basePrice: parseFloat(String(u.basePrice ?? '0')) || 0,
      weekendPrice: parseFloat(String(u.weekendPrice ?? '0')) || null,
      capacity: u.capacity ?? 0,
    }));
  }, [unitsData]);

  // Parse rate plans from response
  const ratePlans = useMemo(() => {
    if (!ratePlansData) return [];
    const raw = ratePlansData as { data?: { id: string; name: string; code: string }[] };
    return raw?.data ?? [];
  }, [ratePlansData]);

  // Generate visible date range
  const dates = useMemo(
    () => generateDateRange(startDate, viewDays),
    [startDate, viewDays],
  );

  // ─── AxisRooms sync overlay (Flow 3) ──────────────────────────────────────
  // One page-level snapshot (never per-cell), indexed by `${unitId}:${date}`
  // for the primary OTA, exactly like the in-memory `overrides` map.
  const syncSnapshot = useSyncSnapshot(
    {
      propertyId: propertyId || '',
      from: format(startDate, 'yyyy-MM-dd'),
      to: format(addDays(startDate, viewDays), 'yyyy-MM-dd'),
      metric: 'rate',
    },
    !!propertyId,
  );

  const syncByCell = useMemo(() => {
    const map = new Map<string, SyncCell>();
    const cells = syncSnapshot.data?.cells ?? [];
    const primary = resolvePrimaryOta(propertyId || '', cells);
    for (const c of cells) {
      if (primary && c.otaId !== primary) continue;
      const key = `${c.unitId}:${c.date}`;
      const existing = map.get(key);
      if (!existing || c.rateplanId.localeCompare(existing.rateplanId) < 0) map.set(key, c);
    }
    return map;
  }, [syncSnapshot.data, propertyId]);

  const neverSynced = !!propertyId && !syncSnapshot.isLoading && (syncSnapshot.data?.cells.length ?? 0) === 0;

  // ─── Cell price resolution ────────────────────────────────────────────────

  const getCellPrice = useCallback(
    (unitId: string, date: Date): { price: number; isOverride: boolean } => {
      const key = `${unitId}:${format(date, 'yyyy-MM-dd')}`;
      const overridePrice = overrides.get(key);
      if (overridePrice !== undefined) {
        return { price: overridePrice, isOverride: true };
      }
      const unit = units.find((u) => u.id === unitId);
      const day = date.getDay();
      const isWeekend = day === 0 || day === 6;
      const price = isWeekend && unit?.weekendPrice ? unit.weekendPrice : (unit?.basePrice ?? 0);
      return { price, isOverride: false };
    },
    [overrides, units],
  );

  // ─── Navigation handlers ─────────────────────────────────────────────────

  const navigateBack = useCallback(() => {
    setStartDate((prev) => subDays(prev, viewDays));
    setDatePickerValue(format(subDays(startDate, viewDays), 'yyyy-MM-dd'));
  }, [viewDays, startDate]);

  const navigateForward = useCallback(() => {
    setStartDate((prev) => addDays(prev, viewDays));
    setDatePickerValue(format(addDays(startDate, viewDays), 'yyyy-MM-dd'));
  }, [viewDays, startDate]);

  const handleDatePickerChange = useCallback((value: string) => {
    setDatePickerValue(value);
    try {
      const parsed = parseISO(value);
      if (!isNaN(parsed.getTime())) {
        setStartDate(parsed);
      }
    } catch {
      // ignore invalid date
    }
  }, []);

  // ─── Cell editing handlers ────────────────────────────────────────────────

  const handleCellClick = useCallback(
    (unitId: string, date: Date) => {
      const { price } = getCellPrice(unitId, date);
      setEditingCell({
        unitId,
        date: format(date, 'yyyy-MM-dd'),
        currentPrice: price,
      });
      setEditValue(String(price));
    },
    [getCellPrice],
  );

  const handleCellSave = useCallback(() => {
    if (!editingCell) return;
    const newPrice = parseFloat(editValue);
    if (isNaN(newPrice) || newPrice < 0) return;

    const key = `${editingCell.unitId}:${editingCell.date}`;

    overrideMutation.mutate(
      {
        unitId: editingCell.unitId,
        newPrice,
        reason: `Rate calendar override for ${editingCell.date}`,
      },
      {
        onSuccess: () => {
          setOverrides((prev) => {
            const next = new Map(prev);
            next.set(key, newPrice);
            return next;
          });
          setEditingCell(null);
          void queryClient.invalidateQueries({ queryKey: ['pricing'] });
          void queryClient.invalidateQueries({ queryKey: ['units'] });
          // The edit pushes back to AxisRooms — repaint the parity badge to
          // push_unconfirmed pending the confirming pull.
          void queryClient.invalidateQueries({ queryKey: ['channels', 'sync'] });
        },
      },
    );
  }, [editingCell, editValue, overrideMutation, queryClient]);

  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  // ─── Bulk edit handlers ───────────────────────────────────────────────────

  const handleBulkApply = useCallback(() => {
    const newPrice = parseFloat(bulkEdit.newPrice);
    if (isNaN(newPrice) || newPrice < 0) return;
    if (bulkEdit.selectedUnitIds.length === 0) return;

    const from = parseISO(bulkEdit.fromDate);
    const to = parseISO(bulkEdit.toDate);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return;

    const promises: Promise<unknown>[] = [];
    const newOverrides = new Map(overrides);

    for (const unitId of bulkEdit.selectedUnitIds) {
      let current = from;
      while (current <= to) {
        const key = `${unitId}:${format(current, 'yyyy-MM-dd')}`;
        newOverrides.set(key, newPrice);
        promises.push(
          overrideMutation.mutateAsync({
            unitId,
            newPrice,
            reason: `Bulk rate calendar override ${bulkEdit.fromDate} to ${bulkEdit.toDate}`,
          }),
        );
        current = addDays(current, 1);
      }
    }

    // Optimistically update overrides
    setOverrides(newOverrides);

    void Promise.all(promises).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['pricing'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
    });

    setBulkEdit((prev) => ({ ...prev, isOpen: false, newPrice: '' }));
  }, [bulkEdit, overrides, overrideMutation, queryClient]);

  const toggleBulkUnit = useCallback((unitId: string) => {
    setBulkEdit((prev) => ({
      ...prev,
      selectedUnitIds: prev.selectedUnitIds.includes(unitId)
        ? prev.selectedUnitIds.filter((id) => id !== unitId)
        : [...prev.selectedUnitIds, unitId],
    }));
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  const isLoading = unitsLoading;

  return (
    <div>
      <PageHeader
        title="Rate Calendar"
        description="Set and manage daily pricing across units"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setBulkEdit((prev) => ({ ...prev, isOpen: !prev.isOpen }))
              }
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors"
            >
              <CalendarDays size={16} aria-hidden="true" />
              Bulk Edit
            </button>
          </div>
        }
      />

      {/* Navigation Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Week navigation */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={navigateBack}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            aria-label="Previous period"
          >
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={datePickerValue}
            onChange={(e) => handleDatePickerChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Start date"
          />
          <button
            type="button"
            onClick={navigateForward}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            aria-label="Next period"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* View days toggle */}
        <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => setViewDays(7)}
            className={cn(
              'px-3 py-1.5 text-sm font-medium transition-colors',
              viewDays === 7
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            7 days
          </button>
          <button
            type="button"
            onClick={() => setViewDays(14)}
            className={cn(
              'px-3 py-1.5 text-sm font-medium transition-colors',
              viewDays === 14
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            14 days
          </button>
        </div>

        {/* Rate plan filter */}
        {ratePlans.length > 0 && (
          <select
            value={selectedRatePlanId}
            onChange={(e) => setSelectedRatePlanId(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Filter by rate plan"
          >
            <option value="">All Rate Plans</option>
            {ratePlans.map((rp) => (
              <option key={rp.id} value={rp.id}>
                {rp.name} ({rp.code})
              </option>
            ))}
          </select>
        )}

        {/* Date range label */}
        <span className="text-sm text-gray-500 ml-auto">
          {format(dates[0], 'MMM d')} &ndash;{' '}
          {format(dates[dates.length - 1], 'MMM d, yyyy')}
        </span>
      </div>

      {/* Bulk Edit Toolbar */}
      {bulkEdit.isOpen && (
        <BulkEditToolbar
          bulkEdit={bulkEdit}
          setBulkEdit={setBulkEdit}
          units={units}
          toggleBulkUnit={toggleBulkUnit}
          onApply={handleBulkApply}
          isApplying={overrideMutation.isPending}
        />
      )}

      {/* AxisRooms not-synced hint (non-blocking) */}
      {neverSynced && units.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          <AlertTriangle size={13} className="shrink-0 text-gray-400" aria-hidden="true" />
          Not synced with AxisRooms yet — rates shown are the derived PMS curve.
          <a href="/channel-manager?tab=sync" className="font-semibold text-primary-600 hover:text-primary-700">Sync in Channels</a>
        </div>
      )}

      {/* Main Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2
            size={32}
            className="animate-spin text-primary-500"
            aria-label="Loading rate calendar"
          />
        </div>
      ) : units.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No units found"
          description="Add units to this property to manage their daily rates."
        />
      ) : (
        <RateGrid
          units={units}
          dates={dates}
          getCellPrice={getCellPrice}
          syncByCell={syncByCell}
          editingCell={editingCell}
          editValue={editValue}
          setEditValue={setEditValue}
          onCellClick={handleCellClick}
          onCellSave={handleCellSave}
          onCellCancel={handleCellCancel}
          isSaving={overrideMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Bulk Edit Toolbar ──────────────────────────────────────────────────────

function BulkEditToolbar({
  bulkEdit,
  setBulkEdit,
  units,
  toggleBulkUnit,
  onApply,
  isApplying,
}: {
  bulkEdit: BulkEditState;
  setBulkEdit: React.Dispatch<React.SetStateAction<BulkEditState>>;
  units: Unit[];
  toggleBulkUnit: (unitId: string) => void;
  onApply: () => void;
  isApplying: boolean;
}) {
  const allSelected = units.length > 0 && bulkEdit.selectedUnitIds.length === units.length;

  return (
    <div className="mb-4 rounded-xl border border-primary-200 bg-primary-50/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">Bulk Rate Override</h3>
        <button
          type="button"
          onClick={() => setBulkEdit((prev) => ({ ...prev, isOpen: false }))}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close bulk edit"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        {/* Date range */}
        <div className="flex items-center gap-2">
          <div>
            <label
              htmlFor="bulk-from"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              From
            </label>
            <input
              id="bulk-from"
              type="date"
              value={bulkEdit.fromDate}
              onChange={(e) =>
                setBulkEdit((prev) => ({ ...prev, fromDate: e.target.value }))
              }
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <span className="text-gray-400 text-sm pt-5">to</span>
          <div>
            <label
              htmlFor="bulk-to"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              To
            </label>
            <input
              id="bulk-to"
              type="date"
              value={bulkEdit.toDate}
              onChange={(e) =>
                setBulkEdit((prev) => ({ ...prev, toDate: e.target.value }))
              }
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Unit multi-select */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Units ({bulkEdit.selectedUnitIds.length} selected)
          </label>
          <div className="flex flex-wrap gap-1 max-w-md">
            <button
              type="button"
              onClick={() => {
                if (allSelected) {
                  setBulkEdit((prev) => ({ ...prev, selectedUnitIds: [] }));
                } else {
                  setBulkEdit((prev) => ({
                    ...prev,
                    selectedUnitIds: units.map((u) => u.id),
                  }));
                }
              }}
              className={cn(
                'rounded-md px-2 py-1 text-xs font-medium border transition-colors',
                allSelected
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
              )}
            >
              All
            </button>
            {units.map((unit) => (
              <button
                key={unit.id}
                type="button"
                onClick={() => toggleBulkUnit(unit.id)}
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium border transition-colors',
                  bulkEdit.selectedUnitIds.includes(unit.id)
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
                )}
              >
                {unit.name}
              </button>
            ))}
          </div>
        </div>

        {/* New rate */}
        <div>
          <label
            htmlFor="bulk-price"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            New Rate ($)
          </label>
          <input
            id="bulk-price"
            type="number"
            min="0"
            step="0.01"
            value={bulkEdit.newPrice}
            onChange={(e) =>
              setBulkEdit((prev) => ({ ...prev, newPrice: e.target.value }))
            }
            placeholder="0.00"
            className="w-28 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Apply button */}
        <button
          type="button"
          onClick={onApply}
          disabled={
            isApplying ||
            bulkEdit.selectedUnitIds.length === 0 ||
            !bulkEdit.newPrice
          }
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isApplying ? (
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <Check size={14} aria-hidden="true" />
          )}
          Apply Override
        </button>
      </div>

      {bulkEdit.selectedUnitIds.length === 0 && (
        <p className="mt-2 flex items-center gap-1 text-xs text-amber-600">
          <AlertTriangle size={12} aria-hidden="true" />
          Select at least one unit to apply bulk pricing.
        </p>
      )}
    </div>
  );
}

// ─── Rate Grid ──────────────────────────────────────────────────────────────

function RateGrid({
  units,
  dates,
  getCellPrice,
  syncByCell,
  editingCell,
  editValue,
  setEditValue,
  onCellClick,
  onCellSave,
  onCellCancel,
  isSaving,
}: {
  units: Unit[];
  dates: Date[];
  getCellPrice: (
    unitId: string,
    date: Date,
  ) => { price: number; isOverride: boolean };
  syncByCell: Map<string, SyncCell>;
  editingCell: EditingCell | null;
  editValue: string;
  setEditValue: (value: string) => void;
  onCellClick: (unitId: string, date: Date) => void;
  onCellSave: () => void;
  onCellCancel: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full" role="grid" aria-label="Rate calendar grid">
          {/* Column headers: dates */}
          <thead>
            <tr className="bg-gray-50">
              <th
                className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px] border-r border-gray-200"
                scope="col"
              >
                Unit
              </th>
              {dates.map((date) => {
                const weekend = isWeekend(date);
                return (
                  <th
                    key={date.toISOString()}
                    scope="col"
                    className={cn(
                      'px-2 py-3 text-center text-xs font-semibold min-w-[90px] border-r border-gray-100',
                      weekend
                        ? 'bg-amber-50/60 text-amber-700'
                        : 'text-gray-500',
                    )}
                  >
                    <div>{format(date, 'EEE')}</div>
                    <div className="font-bold text-[11px]">
                      {format(date, 'MMM d')}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {units.map((unit) => (
              <tr key={unit.id} className="hover:bg-gray-50/50">
                {/* Row header: unit info */}
                <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200">
                  <div className="font-medium text-sm text-gray-900">
                    {unit.name}
                  </div>
                  <div className="text-xs text-gray-400">{unit.type}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Base: {formatCurrency(unit.basePrice)}
                  </div>
                </td>

                {/* Rate cells */}
                {dates.map((date) => {
                  const { price, isOverride } = getCellPrice(unit.id, date);
                  const weekend = isWeekend(date);
                  const isEditing =
                    editingCell !== null &&
                    editingCell.unitId === unit.id &&
                    editingCell.date === format(date, 'yyyy-MM-dd');

                  return (
                    <td
                      key={date.toISOString()}
                      className={cn(
                        'px-1 py-1.5 text-center border-r border-gray-100 relative',
                        weekend && 'bg-amber-50/30',
                      )}
                    >
                      {isEditing ? (
                        <RateCellEditor
                          value={editValue}
                          onChange={setEditValue}
                          onSave={onCellSave}
                          onCancel={onCellCancel}
                          isSaving={isSaving}
                        />
                      ) : (
                        <RateCell
                          price={price}
                          basePrice={unit.basePrice}
                          isOverride={isOverride}
                          sync={syncByCell.get(`${unit.id}:${format(date, 'yyyy-MM-dd')}`)}
                          onClick={() => onCellClick(unit.id, date)}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span
            className="w-3 h-3 rounded bg-green-100 border border-green-300"
            aria-hidden="true"
          />
          Premium (above base)
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-3 h-3 rounded bg-red-100 border border-red-300"
            aria-hidden="true"
          />
          Discount (below base)
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-3 h-3 rounded bg-gray-100 border border-gray-300"
            aria-hidden="true"
          />
          Base price
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full bg-blue-500"
            aria-hidden="true"
          />
          Manual override
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
          Changed on AxisRooms
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-500" aria-hidden="true" />
          Closed on AxisRooms
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden="true" />
          Not confirmed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-gray-400" aria-hidden="true" />
          Couldn&apos;t verify
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-3 h-3 rounded bg-amber-50 border border-amber-200"
            aria-hidden="true"
          />
          Weekend
        </span>
      </div>
    </div>
  );
}

// ─── Rate Cell ──────────────────────────────────────────────────────────────

function RateCell({
  price,
  basePrice,
  isOverride,
  sync,
  onClick,
}: {
  price: number;
  basePrice: number;
  isOverride: boolean;
  sync?: SyncCell | undefined;
  onClick: () => void;
}) {
  // The mirrored AxisRooms value IS the live price when a mirror row exists
  // (master-follower); otherwise fall back to the derived curve. The derived
  // curve stays the base reference for the premium/discount tint.
  const displayPrice = sync?.otaLoadedRate ?? price;
  const closed = sync?.closed ?? false;
  const state = sync?.parityState;
  const showBadge = !!state && isBadgeableState(state);

  const diff = displayPrice - basePrice;
  const bgClass = closed
    ? 'bg-slate-100 text-slate-400 hover:bg-slate-200'
    : diff > 0
      ? 'bg-green-50 text-green-700 hover:bg-green-100'
      : diff < 0
        ? 'bg-red-50 text-red-700 hover:bg-red-100'
        : 'bg-gray-50 text-gray-700 hover:bg-gray-100';

  const label = closed
    ? 'Closed on AxisRooms'
    : `Rate ${formatCurrency(displayPrice)}${isOverride ? ', manually overridden' : ''}${
        state && state !== 'synced' && state !== 'unknown' ? `, ${parityStateLabel(state)}` : ''
      }. Click to edit.`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full rounded-md px-2 py-2 text-xs font-semibold transition-colors cursor-pointer',
        bgClass,
      )}
      aria-label={label}
    >
      <span className={cn(closed && 'line-through')}>{closed ? 'Closed' : formatCurrency(displayPrice)}</span>
      {/* Sync parity marker — top-LEFT, decorative (state is in aria-label);
          never intercepts the edit click (the whole button opens the editor). */}
      {showBadge && (
        <span className="pointer-events-none absolute top-0.5 left-0.5">
          <ParityBadge state={state} size="dot" />
        </span>
      )}
      {/* Existing manual-override dot stays top-RIGHT. */}
      {isOverride && (
        <span
          className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-blue-500"
          title="Manual override"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

// ─── Rate Cell Editor ───────────────────────────────────────────────────────

function RateCellEditor({
  value,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <input
        ref={inputRef}
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full rounded border border-primary-400 px-1.5 py-1 text-xs text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="New rate"
        disabled={isSaving}
      />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className="inline-flex items-center justify-center w-6 h-5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
          aria-label="Save rate"
        >
          {isSaving ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <Check size={10} />
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="inline-flex items-center justify-center w-6 h-5 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 disabled:opacity-50 transition-colors"
          aria-label="Cancel editing"
        >
          <X size={10} />
        </button>
      </div>
    </div>
  );
}
